import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'

const ROOT_TABS = new Set(['/', '/discover', '/search', '/social', '/profile'])
const FESTIVAL_TABS = new Set(['schedule', 'friends', 'map'])

// Festival mode's own tabs are roots too, but its drill-ins (`/totem/:id`,
// `/friends/:uid`) are not — they sit one segment deeper and back has to work
// there, whether or not the push happened to carry `festivalDrillIn` state.
function isFestivalRoot(pathname: string) {
  const [, base, eventId, tab, ...rest] = pathname.replace(/\/$/, '').split('/')
  if (base !== 'live-festival' || !eventId || rest.length > 0) return false
  return tab === undefined || FESTIVAL_TABS.has(tab)
}

function isRootLike(pathname: string) {
  return ROOT_TABS.has(pathname) || isFestivalRoot(pathname)
}

// On iOS native, WKWebView's swipe-back gesture fires history.back(). If the
// history stack has stale entries (from push-based navigations to root tabs, or
// from the OTA bundle switch adding an extra entry), the gesture navigates away
// unexpectedly. Block POP navigations when already at a root tab — the user has
// the tab bar for root-to-root movement. Festival mode's tabs are blocked the
// same way — the only exit path is the explicit exit button in its bottom nav.
export function useBlockBackAtRoot() {
  const isNative = Capacitor.isNativePlatform()

  const blocker = useBlocker(({ currentLocation, historyAction }) => {
    if (!isNative || historyAction !== 'POP') return false
    if (!isRootLike(currentLocation.pathname)) return false
    // Allow swipe-back from a festival drill-in (set tap, friend tap)
    if ((currentLocation.state as Record<string, unknown>)?.festivalDrillIn) return false
    return true
  })

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])
}
